import PropTypes from "prop-types"
import { map, chain } from "lodash"
import useResource from "hooks/resources"

const CONTENT_SET_QUERY = `query ($contentSetId: String!) {
  contentSet (id: $contentSetId) {
    name
    factors {
      id
      name
      category
    }
    categories {
      id
      name
    }
  }
}`

const CONTENT_SETS_QUERY = `{
  contentSets {
    id
    name
    description
    proprietary
    dataType
    factors {
      id
      name
      category
    }
    categories {
      id
      name
    }
  }
}`

export const CONTENT_SET_METADATA = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  proprietary: PropTypes.bool,
  dataType: PropTypes.oneOf(["sensitivity"]),
  factors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
  })).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
}

export const CONTENT_SETS_METADATA_QUERY = {
  resource: "contentSets",
  url: () => ({
    id: "contentSets",
    query: CONTENT_SETS_QUERY,
  }),
}

export function useContentSetsMetadata() {
  return useResource(CONTENT_SETS_METADATA_QUERY, "contentSets")
}

export function useContentSetMetadata(contentSetId) {
  return useResource({
    resource: `contentSetMetadata-${contentSetId}`,
    url: () => {
      if (!contentSetId) {
        return null
      }
      return {
        id: `contentSetMetadata-${contentSetId}`,
        query: CONTENT_SET_QUERY,
        variables: { contentSetId },
      }
    },
  }, "contentSet")
}

export function useContentSetMetrics(contentSetId) {
  const [contentSetMetadata, status] = useContentSetMetadata(contentSetId)

  const metrics = map(contentSetMetadata?.categories, ({ id: categoryId, name: categoryLabel }) => ({
    id: categoryId,
    label: categoryLabel,
    options: chain(contentSetMetadata.factors)
      .filter(({ category: factorCategoryId }) => factorCategoryId === categoryId)
      .map(({ name: label, ...factor }) => ({
        label,
        ...factor,
      }))
      .value(),
  }))
  return [metrics, status]
}
